import { useQuery } from '@tanstack/react-query';

import type { RequestError } from '../../errors/request.error';
import { PermissionScope } from '../../interfaces/permissions';
import type { BranchReference, UserBranchSource } from '../../interfaces/branches';
import { parseCurrentUserResponse } from '../../utils/current-user-response';
import { useRequest } from '../use-request';

export interface UsersCurrentEffectivePermission {
  readonly name: string;
  readonly scopes: PermissionScope[];
}

export interface UsersCurrentData {
  readonly id?: string;
  readonly organisation?: { readonly type?: string | null } | null;
  readonly canAccessCaseAttachments?: boolean;
  readonly canUseSupportAssistant?: boolean;
  readonly email?: string;
  readonly name: string;
  readonly roleName?: string | null;
  readonly branches?: BranchReference[];
  readonly assignedBranches?: BranchReference[];
  readonly branchSource?: UserBranchSource;
  readonly isImpersonating?: boolean;
  readonly effectivePermissions: UsersCurrentEffectivePermission[];
}

export const useUsersCurrent = (enabled: boolean) => {
  const request = useRequest({ authenticate: true });

  return useQuery<UsersCurrentData, RequestError>({
    queryKey: ['users', 'current'],
    queryFn: async () =>
      parseCurrentUserResponse(await request<unknown>({
        method: 'GET',
        path: '/api/users/current',
      })),
    enabled,
    retry: false,
  });
};
